import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    signal,
} from '@angular/core';
import { NzIconModule } from 'ng-zorro-antd/icon';
import {
    DrawerService,
    LayoutModule,
    ModalService,
    SharedUiModule,
} from '@pf/shared-ui';
import { ProfileProvider } from '@pf/shared-common';
import { EquipmentDto, EquipmentStatusOptions } from '@pf/platform-wms/domain';
import {
    NzDrawerComponent,
    NzDrawerContentDirective,
} from 'ng-zorro-antd/drawer';
import { SharedUtilPlatformModule } from '@pf/shared/util-platform';
import {
    CpwEquipmentScanComponent,
    EquipmentScanEvent,
} from './cpw-equipment-scan.component';
import { asArraySafe, asNumberSafe } from '@pf/shared-utility';
import { BrowserStorageService } from '@pf/shared-services';
import {
    containerHasAssignedBundles,
    HandlingUnitAssignmentService,
    isHandlingLtl,
} from '@cpw/domain';
import { toSignal } from '@angular/core/rxjs-interop';
import { EquipmentStatus, ZoneType } from '@control-tower/platform-wms';
import { CpwStagingService } from '../services/cpw-staging.service';
import {
    CpwEquipmentInfoDrawerComponent,
    CpwEquipmentInfoDrawerData,
} from './cpw-equipment-info-drawer.component';

type EquipmentViewModel = {
    equipment: EquipmentDto;
    displayName: string;
    status: EquipmentStatus;
    statusColor: string;
    usedChildCount: number;
    childCount: number;
    isLtl: boolean;
    isAssignedToCurrentUser: boolean;
};

@Component({
    selector: 'cpw-equipment-overview',
    standalone: true,
    imports: [
        LayoutModule,
        NzIconModule,
        SharedUiModule,
        NzDrawerComponent,
        SharedUtilPlatformModule,
        CpwEquipmentScanComponent,
        NzDrawerContentDirective,
    ],
    templateUrl: './cpw-equipment-overview.component.html',
    styleUrl: './cpw-equipment-overview.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwEquipmentOverviewComponent {
    private readonly modalService = inject(ModalService);
    private readonly drawerService = inject(DrawerService);
    private readonly stagingService = inject(CpwStagingService);
    private readonly handlingUnitService = inject(
        HandlingUnitAssignmentService
    );
    private readonly profileProvider = inject(ProfileProvider);
    private readonly storageService = inject(BrowserStorageService);

    canStageLtl = input<boolean>(false);
    moveToZoneType = input<ZoneType | null>(null);
    showAddButton = input(true);
    showRemoveButton = input(true);
    showChildCount = input(true);
    validationRule = input<'Available' | 'Staged'>('Available');
    addHandlingUnitVisible = signal(false);

    /** Collapse the card by default when there's no stored user preference. */
    defaultCollapsed = input(false);

    private static readonly COLLAPSED_KEY = 'pf-equipment-overview-collapsed';
    collapsed = signal<boolean>(this.readCollapsed());

    constructor() {
        // Apply the caller's default only when the user hasn't toggled it
        // themselves (no stored preference). User toggles persist and win.
        effect(() => {
            const def = this.defaultCollapsed();
            if (!this.hasStoredCollapsed()) {
                this.collapsed.set(def);
            }
        });
    }

    toggleCollapsed() {
        this.collapsed.update(v => !v);
        this.writeCollapsed(this.collapsed());
    }

    private hasStoredCollapsed(): boolean {
        return (
            this.storageService.getItem<boolean>(
                CpwEquipmentOverviewComponent.COLLAPSED_KEY,
                'local'
            ) != null
        );
    }

    private readCollapsed(): boolean {
        return (
            this.storageService.getItem<boolean>(
                CpwEquipmentOverviewComponent.COLLAPSED_KEY,
                'local'
            ) ?? false
        );
    }

    private writeCollapsed(collapsed: boolean): void {
        if (collapsed) {
            this.storageService.setItem(
                CpwEquipmentOverviewComponent.COLLAPSED_KEY,
                true,
                'local'
            );
        } else {
            this.storageService.removeItem(
                CpwEquipmentOverviewComponent.COLLAPSED_KEY,
                'local'
            );
        }
    }

    moveToZoneTypeButtonText = computed(() => {
        const zoneType = this.moveToZoneType();
        if (!zoneType) {
            return '';
        }
        return zoneType === ZoneType.Normal
            ? 'Set to Available'
            : `Move to ${zoneType}`;
    });

    moveAllToZoneTypeButtonText = computed(() => {
        const zoneType = this.moveToZoneType();
        if (!zoneType) {
            return '';
        }
        return zoneType === ZoneType.Normal
            ? 'Set all Available'
            : `Move all to ${zoneType}`;
    });

    targetStatus = computed(() => {
        const zoneType = this.moveToZoneType();
        return zoneType === ZoneType.Normal
            ? EquipmentStatus.Available
            : EquipmentStatus.Staged;
    });

    canStage = computed(() => {
        const zoneType = this.moveToZoneType();
        if (!zoneType || this.equipment().length === 0) {
            return false;
        }
        return this.equipment().some(
            equipment =>
                equipment.status !== this.targetStatus() &&
                (equipment.isLtl ? this.canStageLtl() : true)
        );
    });

    private readonly assignedEquipment = toSignal(
        this.handlingUnitService.assignedHandlingUnits$,
        { initialValue: [] as EquipmentDto[] }
    );

    equipment = computed(() =>
        asArraySafe(this.assignedEquipment())
            .sort((a, b) =>
                // sort modified date desc
                new Date(a.modifiedDate as string) >
                new Date(b.modifiedDate as string)
                    ? -1
                    : 1
            )
            .map(
                eq =>
                    ({
                        equipment: eq,
                        displayName: `${eq.equipmentType?.name} ${eq.barcode}`,
                        status: eq.status,
                        statusColor:
                            (eq.status &&
                                EquipmentStatusOptions[eq.status]?.color) ??
                            EquipmentStatusOptions.defaultOption.color,
                        isLtl: isHandlingLtl(eq.equipmentType),
                        isAssignedToCurrentUser:
                            !!eq.assignedToUser &&
                            eq.assignedToUser.id ===
                                this.profileProvider.profile?.userId,
                        usedChildCount: asNumberSafe(
                            asArraySafe(eq.containers)?.filter(child =>
                                containerHasAssignedBundles(child)
                            ).length
                        ),
                        childCount: asNumberSafe(
                            asArraySafe(eq.containers)?.length
                        ),
                    }) as EquipmentViewModel
            )
    );

    equipmentByType = computed(() => {
        const grouped = new Map<
            string,
            { typeName: string; items: EquipmentViewModel[] }
        >();
        for (const eq of this.equipment()) {
            const typeId = eq.equipment.equipmentType?.id ?? 'unknown';
            const typeName = eq.equipment.equipmentType?.name ?? 'Unknown';
            if (!grouped.has(typeId)) {
                grouped.set(typeId, { typeName, items: [] });
            }
            grouped.get(typeId)!.items.push(eq);
        }
        return Array.from(grouped.values());
    });

    openAddHandlingUnit() {
        this.addHandlingUnitVisible.set(true);
    }

    closeAddHandlingUnit() {
        this.addHandlingUnitVisible.set(false);
    }

    onEquipmentScanned(event: EquipmentScanEvent) {
        this.handlingUnitService
            .addHandlingUnit$(event.equipment)
            .subscribe(success => event.processed.next(success));
        this.closeAddHandlingUnit();
    }

    onRemove(equipment: EquipmentDto) {
        this.modalService.confirmAction(
            'Handling Unit',
            () => {
                this.handlingUnitService
                    .removeHandlingUnit$(equipment)
                    .subscribe();
            },
            'Release'
        );
    }

    protected stageEquipment(equipment: EquipmentDto) {
        const zoneType = this.moveToZoneType();
        if (!zoneType) {
            return;
        }
        this.stagingService.showDrawer(
            { zoneType, equipmentIds: [equipment.id] },
            success => {
                if (success) {
                    this.handlingUnitService
                        .releaseHandlingUnits$([equipment.id])
                        .subscribe();
                }
            }
        );
    }

    protected stageAllEquipment() {
        const zoneType = this.moveToZoneType();
        const equipmentIds = this.equipment().map(e => e.equipment.id);
        if (!zoneType || !equipmentIds.length) {
            return;
        }

        this.stagingService.showDrawer(
            { zoneType, equipmentIds: equipmentIds },
            success => {
                if (success) {
                    this.handlingUnitService
                        .releaseHandlingUnits$(equipmentIds)
                        .subscribe();
                }
            }
        );
    }

    protected showEquipmentInfo(equipment: EquipmentDto) {
        this.drawerService.createTemplateDrawer({
            title: `${equipment.equipmentType?.name} ${equipment.barcode} - ${equipment.status}`,
            content: CpwEquipmentInfoDrawerComponent,
            data: { equipment } as CpwEquipmentInfoDrawerData,
            size: 'default',
            isMobile: true,
        });
    }

    protected onEquipmentChipClick(eq: EquipmentViewModel) {
        this.showEquipmentInfo(eq.equipment);
    }
}
